import { HotjarEvents, submitEvent } from 'hotjar';
import * as React from 'react';
import { toast } from 'react-toastify';
import { useObservable } from 'react-use-observable';
import eduzzAccountsService from 'v2/services/eduzzAccount';
import financialService from 'v2/services/financial';
import { npsService } from 'v2/services/nps';
import uploadService from 'v2/services/upload';

import type { AddBankAccountModel } from 'v2/interfaces/financial';

import { Breadcrumb } from './Breadcrumb';
import { Stepper } from './Stepper';
import { CAIXA_ECONOMICA_ID } from './Stepper/form';

import type { NewBankAccount } from './Stepper/form';
import type { FormikHelpers } from 'formik';

type VerifyProps = {
  accountBankId: number;
  verificationCode: string;
  setSubmitting: (isSubmitting: boolean) => void;
  setStatus: (status?: string) => void;
};

const NEW_BANK_ACCOUNT_SURVEY_ID = 958174;
const NEW_BANK_ACCOUNT_SURVEY_EXPIRATION_DAYS = 90;

function AddBankAccount() {
  const [user] = useObservable(() => eduzzAccountsService.getUser(), []);
  const [betaModules] = useObservable(() => eduzzAccountsService.getBetaModules(), []);

  const setHotjarAddBankAccountSurvey = React.useCallback(async () => {
    const isWhiteBelt = !user.is_elite;
    if (!isWhiteBelt) return;
    const lastAnswer = await npsService.getSurveyLastAnswer(NEW_BANK_ACCOUNT_SURVEY_ID);
    if (!lastAnswer) return;
    const isSurveyExpired = lastAnswer.answered && lastAnswer.days_count >= NEW_BANK_ACCOUNT_SURVEY_EXPIRATION_DAYS;
    const shouldShowSurvey = !lastAnswer.answered || isSurveyExpired;
    if (!shouldShowSurvey) return;
    submitEvent(HotjarEvents.NEW_BANK_ACCOUNT_CREATED);
  }, [user]);

  const uploadImage = React.useCallback(async (image: File) => {
    const upload = await uploadService.uploadFile(image, 'mpg_docs');
    return upload.data.upload_id;
  }, []);

  const verifyBankAccount = React.useCallback(
    async ({ accountBankId, verificationCode, setSubmitting, setStatus }: VerifyProps) => {
      try {
        setSubmitting(true);
        await financialService.verifyBankAccount(accountBankId, verificationCode);
        sessionStorage.removeItem('form_submitted');
        setStatus('verified');
        setHotjarAddBankAccountSurvey();
      } catch (err) {
        toast.error(err);
        console.error(err);
      } finally {
        setSubmitting(false);
      }
    },
    [setHotjarAddBankAccountSurvey]
  );

  const handleSubmit = React.useCallback(
    async (values: NewBankAccount, { setSubmitting, setStatus, setFieldValue }: FormikHelpers<NewBankAccount>) => {
      if (!sessionStorage.form_submitted) {
        const getRequest = (formData: AddBankAccountModel) => {
          if (values?.id) return financialService.editBankAccount(formData);
          return financialService.addBankAccount(formData);
        };

        try {
          setSubmitting(true);
          const { cnh_image, bank_receipt_image, selfie_video, rg_front_image, rg_back_image, personal_document, ...rest } = values;
          const model = { ...rest } as AddBankAccountModel;

          const savedFiles = sessionStorage?.inputFiles && JSON.parse(sessionStorage.inputFiles);

          const savedRgFront = savedFiles?.personal_document_image_front;
          const savedRgBack = savedFiles?.personal_document_image_back;
          const savedCnh = savedFiles?.driver_license_image;
          const savedAddressImage = savedFiles?.address_image;
          const savedDocumentVideo = savedFiles?.personal_document_video;

          const noSavedDocuments = !savedRgFront && !savedRgBack && !savedCnh;
          const noNewDocuments = !rg_front_image && !rg_back_image && !cnh_image;
          const needDocuments = noNewDocuments && noSavedDocuments;
          const needAddressImage = !bank_receipt_image && !savedAddressImage;
          const needVideo = !selfie_video && !savedDocumentVideo;

          if (needDocuments) throw 'Por favor, nos envie alguma foto de documento!';
          if (needVideo) throw 'Por favor, nos envie um vídeo de validação!';
          if (needAddressImage) throw 'Por favor, nos envie um comprovante bancário!';

          if (savedRgFront) model.personal_document_image_front = savedRgFront;
          if (savedRgBack) model.personal_document_image_back = savedRgBack;
          if (savedCnh) model.driver_license_image = savedCnh;
          if (savedAddressImage) model.address_image = savedAddressImage;
          if (savedDocumentVideo) model.personal_document_video = savedDocumentVideo;

          if (rg_front_image && rg_back_image && personal_document === 'RG') {
            const [frontId, backId] = await Promise.all([uploadImage(rg_front_image), uploadImage(rg_back_image)]);
            model.personal_document_image_front = frontId;
            model.personal_document_image_back = backId;
          }
          if (cnh_image && personal_document === 'CNH') {
            model.driver_license_image = await uploadImage(cnh_image);
          }
          if (bank_receipt_image) {
            model.address_image = await uploadImage(bank_receipt_image);
          }
          if (selfie_video) {
            model.personal_document_video = await uploadImage(selfie_video);
          }
          if (values.bank_id !== CAIXA_ECONOMICA_ID) {
            delete model.operation_id;
          }

          Object.keys(model).forEach((key) => {
            if (model[key] === null) {
              delete model[key];
            }
          });

          await getRequest(model).then((data) => setFieldValue('id', data.fund_transfer.id));

          sessionStorage.setItem('form_submitted', 'true');
          sessionStorage.removeItem('inputFiles');
          setStatus('submitted');
        } catch (err) {
          toast.error(err);
          console.error(err);
        } finally {
          setSubmitting(false);
        }

        return;
      }

      if (!!sessionStorage.form_submitted) {
        verifyBankAccount({ accountBankId: values.id, verificationCode: values.verify_code, setSubmitting, setStatus });
      }
    },
    [uploadImage, verifyBankAccount]
  );

  React.useEffect(() => {
    return () => {
      sessionStorage.removeItem('form_submitted');
      sessionStorage.removeItem('inputFiles');
    };
  }, []);

  if (!betaModules) return null;

  return (
    <div>
      <Breadcrumb />
      <Stepper handleSubmit={handleSubmit} />
    </div>
  );
}

export { AddBankAccount };
